import { formatDate, parseISO } from 'date-fns';
import { Conflict } from '@components/schedules/dialogs/internal/publish/types';
import { QueryParams } from '@hooks/queryParams/types';
import { WorkflowsDropdown } from '@services/dropdowns/workflows/types';
import {
  WorkloadCalendarSchedule,
  WorkloadDraftsItems,
  WorkloadForeman,
  WorkloadMonthlyCalendarItems,
} from './types';

const DAY_KEY_FORMAT = 'yyyy-MM-dd';

const restrictToVisibleDates = (
  foremen: WorkloadForeman[],
  withoutTeam: WorkloadCalendarSchedule[],
  visibleDates?: Date[],
): { foremen: WorkloadForeman[]; withoutTeam: WorkloadCalendarSchedule[] } => {
  if (!visibleDates?.length) return { foremen, withoutTeam };

  const visibleDays = new Set(visibleDates.map((date) => formatDate(date, DAY_KEY_FORMAT)));
  const isVisible = (dateStr?: string) =>
    Boolean(dateStr && visibleDays.has(formatDate(parseISO(dateStr), DAY_KEY_FORMAT)));

  return {
    foremen: foremen.map((foreman) => ({
      ...foreman,
      schedules: foreman.schedules.filter((schedule) => isVisible(schedule.startDate)),
    })),
    withoutTeam: withoutTeam.filter((schedule) => isVisible(schedule.startDate)),
  };
};

type FilterableSchedule = Pick<
  WorkloadCalendarSchedule,
  'job' | 'specialty' | 'status' | 'warning' | 'taskUnitIdentityId'
>;

const matchesAssignee = (selectedIds: string[] | undefined, actualId: string | undefined) => {
  if (!selectedIds?.length) return true;
  if (selectedIds.includes(actualId || '')) return true;

  return selectedIds.includes('unassigned') && !actualId;
};

const matchesDivisionIds = (selectedIds: string[] | undefined, actualIds: string[] | undefined) => {
  if (!selectedIds?.length) return true;
  if ((actualIds || []).some((id) => selectedIds.includes(id))) return true;

  return selectedIds.includes('unassigned') && !(actualIds || []).length;
};

export const expandWorkflowIds = (
  selectedIds: string[] | undefined,
  workflows: Pick<WorkflowsDropdown, 'id' | 'previousWorkflowsIds'>[],
): string[] | undefined => {
  if (!selectedIds?.length) return selectedIds;

  const previousIds = workflows
    .filter((workflow) => selectedIds.includes(workflow.id))
    .flatMap((workflow) => workflow.previousWorkflowsIds || []);

  return Array.from(new Set([...selectedIds, ...previousIds]));
};

export const workloadJobFiltersMatch = (
  schedule: FilterableSchedule,
  params: QueryParams,
): boolean => {
  const {
    divisionIds,
    workflowIds,
    taskIdentityIds,
    team: taskSpecialtyIds,
    status,
    builderIds,
    projectManagerIds,
    coordinatorIds,
  } = params;

  if (divisionIds?.length && !divisionIds.includes(schedule.job.division?.id || '')) return false;
  if (workflowIds?.length && !workflowIds.includes(schedule.job.workflow?.id || '')) return false;
  if (taskIdentityIds?.length && !taskIdentityIds.includes(schedule.taskUnitIdentityId || '')) {
    return false;
  }
  if (taskSpecialtyIds?.length && !taskSpecialtyIds.includes(schedule.specialty?.value || '')) {
    return false;
  }
  if (status?.length) {
    const matchesStatus = status.some((s) =>
      s === 'warning' ? schedule.warning : schedule.status === s,
    );
    if (!matchesStatus) return false;
  }
  if (builderIds?.length && !builderIds.includes(schedule.job.builder?.id || '')) return false;
  if (!matchesAssignee(projectManagerIds, schedule.job.projectManager?.id)) return false;
  if (!matchesAssignee(coordinatorIds, schedule.job.coordinator?.id)) return false;

  return true;
};

type ResourceFilterableSchedule = Pick<
  WorkloadCalendarSchedule,
  'foremanId' | 'foremanDivisionIds' | 'specialties'
>;

export const workloadResourceFiltersMatch = (
  schedule: ResourceFilterableSchedule,
  params: QueryParams,
): boolean => {
  const { resourceDivisionIds, resourceSpecialtyIds, fieldUserIds } = params;

  if (!matchesDivisionIds(resourceDivisionIds, schedule.foremanDivisionIds)) return false;
  if (
    resourceSpecialtyIds?.length &&
    !(schedule.specialties || []).some((specialty) =>
      resourceSpecialtyIds.includes(specialty.value),
    )
  ) {
    return false;
  }
  if (fieldUserIds?.length && !fieldUserIds.includes(schedule.foremanId)) return false;

  return true;
};

type ResourceFilterableForeman = Pick<
  WorkloadForeman,
  'id' | 'avatarType' | 'divisionIds' | 'specialties'
>;

const explicitResourceIds = (
  avatarType: ResourceFilterableForeman['avatarType'],
  params: QueryParams,
): string[] | undefined => {
  const { fieldUserIds, crewMemberIds, equipmentIds } = params;

  if (avatarType === 'field_user') return fieldUserIds;
  if (avatarType === 'crew_member') return crewMemberIds;
  if (avatarType === 'equipment_member') return equipmentIds;
  return undefined;
};

const isExplicitlySelectedResource = (
  foreman: ResourceFilterableForeman,
  params: QueryParams,
): boolean => {
  const idsForType = explicitResourceIds(foreman.avatarType, params);
  return Boolean(idsForType?.length && idsForType.includes(foreman.id));
};

const hasExplicitResourceSelection = (params: QueryParams): boolean => {
  const { fieldUserIds, crewMemberIds, equipmentIds } = params;
  return Boolean(fieldUserIds?.length || crewMemberIds?.length || equipmentIds?.length);
};

export const workloadResourceFiltersMatchForeman = (
  foreman: ResourceFilterableForeman,
  params: QueryParams,
): boolean => {
  const { crewTypes, resourceDivisionIds, resourceSpecialtyIds } = params;

  if (!matchesDivisionIds(resourceDivisionIds, foreman.divisionIds)) return false;
  if (
    resourceSpecialtyIds?.length &&
    !(foreman.specialties || []).some((specialty) => resourceSpecialtyIds.includes(specialty.value))
  ) {
    return false;
  }

  if (hasExplicitResourceSelection(params)) return isExplicitlySelectedResource(foreman, params);

  if (crewTypes?.length && foreman.avatarType && !crewTypes.includes(foreman.avatarType)) {
    return false;
  }

  return true;
};

export interface WorkloadWeeklyHiddenRecords {
  visibleForemen: WorkloadForeman[];
  visibleWithoutTeam: WorkloadCalendarSchedule[];
  hiddenCount: number;
  hiddenForemenCount: number;
}

export const filterWorkloadWeeklyRecords = (
  rawForemen: WorkloadForeman[],
  rawWithoutTeam: WorkloadCalendarSchedule[],
  params: QueryParams,
  visibleDates?: Date[],
): WorkloadWeeklyHiddenRecords => {
  const { foremen, withoutTeam } = restrictToVisibleDates(rawForemen, rawWithoutTeam, visibleDates);

  let hiddenCount = 0;
  let hiddenForemenCount = 0;

  const visibleForemen = foremen.reduce<WorkloadForeman[]>((acc, foreman) => {
    if (!workloadResourceFiltersMatchForeman(foreman, params)) {
      hiddenCount += foreman.schedules.length;
      hiddenForemenCount += 1;
      return acc;
    }

    const visibleSchedules = foreman.schedules.filter((schedule) =>
      workloadJobFiltersMatch(schedule, params),
    );
    hiddenCount += foreman.schedules.length - visibleSchedules.length;

    acc.push({ ...foreman, schedules: visibleSchedules });
    return acc;
  }, []);

  const visibleWithoutTeam = withoutTeam.filter((schedule) =>
    workloadJobFiltersMatch(schedule, params),
  );
  hiddenCount += withoutTeam.length - visibleWithoutTeam.length;

  return { visibleForemen, visibleWithoutTeam, hiddenCount, hiddenForemenCount };
};

export interface WorkloadMonthlyHiddenRecords {
  visibleRecords: WorkloadMonthlyCalendarItems;
  hiddenCount: number;
  hiddenDaysCount: number;
}

export const filterWorkloadMonthlyRecords = (
  records: WorkloadMonthlyCalendarItems,
  params: QueryParams,
): WorkloadMonthlyHiddenRecords => {
  let hiddenCount = 0;
  let hiddenDaysCount = 0;
  const visibleRecords: WorkloadMonthlyCalendarItems = {};

  Object.entries(records).forEach(([date, schedules]) => {
    const visible = schedules.filter(
      (schedule) =>
        workloadJobFiltersMatch(schedule, params) && workloadResourceFiltersMatch(schedule, params),
    );
    const hiddenForDay = schedules.length - visible.length;

    if (hiddenForDay > 0) {
      hiddenCount += hiddenForDay;
      hiddenDaysCount += 1;
    }
    // eslint-disable-next-line immutable/no-mutation
    visibleRecords[date] = visible;
  });

  return { visibleRecords, hiddenCount, hiddenDaysCount };
};

const isEmptyConflict = (conflict: Conflict): boolean => Object.keys(conflict).length === 0;

const toConflictArray = (raw: Conflict | Conflict[] | null | undefined): Conflict[] => {
  if (!raw) return [];
  const conflicts = Array.isArray(raw) ? raw : [raw];
  return conflicts.filter((conflict) => !isEmptyConflict(conflict));
};

const uniqStrings = (values: string[]): string[] => Array.from(new Set(values));

const uniqConflicts = (conflicts: Conflict[]): Conflict[] => {
  const seen = new Set<string>();
  return conflicts.filter((conflict) => {
    const key = JSON.stringify(conflict);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const computeMonthlyDrafts = (
  records: WorkloadMonthlyCalendarItems,
): WorkloadDraftsItems => {
  const drafts: WorkloadDraftsItems = {};

  Object.entries(records).forEach(([date, schedules]) => {
    const draftSchedules = schedules.filter((s) => s.status === 'draft' && s.foremanId);

    // eslint-disable-next-line immutable/no-mutation
    drafts[date] = {
      total: draftSchedules.length,
      totalSchedules: schedules.length,
      uniqueForemenIds: uniqStrings(draftSchedules.map((s) => s.foremanId)),
      crewsWithPhoneIds: uniqStrings(draftSchedules.flatMap((s) => s.crewWithPhoneIds || [])),
      crewsWithoutPhoneIds: uniqStrings(draftSchedules.flatMap((s) => s.crewWithoutPhoneIds || [])),
      fieldUserAsCrewIds: uniqStrings(draftSchedules.flatMap((s) => s.fieldUserAsCrewIds || [])),
      schedulesIds: draftSchedules.map((s) => s.id),
      conflicts: uniqConflicts(
        draftSchedules.flatMap((s) => [
          ...toConflictArray(s.scheduledForemanConflicts),
          ...toConflictArray(s.scheduledMembersConflicts),
        ]),
      ),
    };
  });

  return drafts;
};

export const computeWeeklyDrafts = (foremen: WorkloadForeman[]): WorkloadDraftsItems => {
  const scheduleIdsByDate = new Map<string, Set<string>>();
  const draftEntriesByDate = new Map<
    string,
    { foremanId: string; schedule: WorkloadCalendarSchedule }[]
  >();

  foremen.forEach((foreman) => {
    foreman.schedules.forEach((schedule) => {
      const date = schedule.startDate;
      if (!date) return;

      if (!scheduleIdsByDate.has(date)) scheduleIdsByDate.set(date, new Set());
      scheduleIdsByDate.get(date)?.add(schedule.id);

      if (foreman.avatarType !== 'field_user' || schedule.status !== 'draft' || !foreman.id) return;

      if (!draftEntriesByDate.has(date)) draftEntriesByDate.set(date, []);
      draftEntriesByDate.get(date)?.push({ foremanId: foreman.id, schedule });
    });
  });

  const drafts: WorkloadDraftsItems = {};

  draftEntriesByDate.forEach((entries, date) => {
    const schedules = entries.map((entry) => entry.schedule);
    const draftSchedules = schedules.filter((s) => s.status === 'draft' && s.foremanId);

    // eslint-disable-next-line immutable/no-mutation
    drafts[date] = {
      total: draftSchedules.length,
      totalSchedules: scheduleIdsByDate.get(date)?.size || 0,
      uniqueForemenIds: uniqStrings(draftSchedules.map((entry) => entry.foremanId)),
      crewsWithPhoneIds: uniqStrings(draftSchedules.flatMap((s) => s.crewWithPhoneIds || [])),
      crewsWithoutPhoneIds: uniqStrings(draftSchedules.flatMap((s) => s.crewWithoutPhoneIds || [])),
      fieldUserAsCrewIds: uniqStrings(draftSchedules.flatMap((s) => s.fieldUserAsCrewIds || [])),
      schedulesIds: uniqStrings(draftSchedules.map((s) => s.id)),
      conflicts: uniqConflicts(
        draftSchedules.flatMap((s) => [
          ...toConflictArray(s.scheduledForemanConflicts),
          ...toConflictArray(s.scheduledMembersConflicts),
        ]),
      ),
    };
  });

  return drafts;
};
